import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { getResponsiveSpacingPixels } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/spacing";
import { SDUIComponentInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import {
  assignInlineVars,
  DynamicVariable,
  mapResponsivePropToCSSVars,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { HorizontalLine } from "@faire/web/slate/Layout";
import { cn } from "@faire/web/tools/cn";
import React from "react";

const varMobileHeight: DynamicVariable<"--f-sdui-spacer"> = {
  variable: "--f-sdui-spacer",
  className: "min-h-(--f-sdui-spacer) basis-(--f-sdui-spacer)",
};

const varTabletHeight: DynamicVariable<"--f-sdui-spacer-md"> = {
  variable: "--f-sdui-spacer-md",
  className: "md:min-h-(--f-sdui-spacer-md) md:basis-(--f-sdui-spacer-md)",
};

const varDesktopHeight: DynamicVariable<"--f-sdui-spacer-lg"> = {
  variable: "--f-sdui-spacer-lg",
  className: "lg:min-h-(--f-sdui-spacer-lg) lg:basis-(--f-sdui-spacer-lg)",
};

const varXLargeHeight: DynamicVariable<"--f-sdui-spacer-xl"> = {
  variable: "--f-sdui-spacer-xl",
  className: "xl:min-h-(--f-sdui-spacer-xl) xl:basis-(--f-sdui-spacer-xl)",
};

export const SDUISpacerRenderer: React.FC<
  SDUIComponentInstanceRendererProps
> = ({ component }) => {
  const spacerComponent = component.spacer_component;
  if (!spacerComponent) {
    return null;
  }
  const { vertical_spacing, responsive_vertical_spacing, style } =
    spacerComponent;
  const variant = style?.variant;
  const responsiveValues = getResponsiveSpacingPixels(
    vertical_spacing,
    responsive_vertical_spacing
  );
  if (!variant || !responsiveValues.mobileAndAbove) {
    return null;
  }

  const { vars: heightVars, classes: heightClasses } =
    mapResponsivePropToCSSVars(responsiveValues, {
      mobileAndAbove: varMobileHeight,
      tabletAndAbove: varTabletHeight,
      desktopAndAbove: varDesktopHeight,
      xLargeAndAbove: varXLargeHeight,
    });

  return (
    <div
      data-test-id="sdui-spacer"
      className={cn("flex flex-col justify-center", heightClasses)}
      style={assignInlineVars(heightVars)}
    >
      {variant === "HORIZONTAL_LINE" ? (
        <HorizontalLine
          color={
            style?.color !== undefined
              ? getSlateColorToken(style.color)
              : undefined
          }
        />
      ) : null}
    </div>
  );
};
